<template>
    <div class="cw-wizard">
        <!-- Loading -->
        <div v-if="loading" class="cw-loading">
            <i class="fal fa-spinner-third fa-spin"></i>
        </div>

        <template v-else-if="form">
            <!-- Form header -->
            <header class="cw-form-header">
                <h1 class="cw-form-title">{{ formTitle }}</h1>
                <div v-if="formDescription" class="cw-form-desc" v-html="formDescription"></div>
            </header>

            <!-- Stepper -->
            <div class="cw-stepper">
                <div
                    v-for="(step, index) in visibleSteps"
                    :key="step.id"
                    class="cw-stepper-item"
                    :class="{
                        'is-active': activeStep === index,
                        'is-done': activeStep > index,
                        'is-clickable': completedSteps[index]
                    }"
                    @click="goToStep(index)"
                >
                    <div class="cw-stepper-icon">
                        <i v-if="activeStep > index" class="fal fa-check"></i>
                        <span v-else>{{ index + 1 }}</span>
                    </div>
                    <span class="cw-stepper-label">{{ getStepName(step) }}</span>
                    <div v-if="index < visibleSteps.length - 1" class="cw-stepper-line"></div>
                </div>
            </div>

            <!-- Step content -->
            <div class="cw-content">
                <transition appear mode="out-in" name="cw-slide">
                    <!-- Form steps -->
                    <wizard-step
                        v-if="activeStep < visibleSteps.length"
                        :key="activeStep"
                        ref="currentStep"
                        :step="visibleSteps[activeStep]"
                        :step-index="activeStep"
                        :answers="answers"
                        :locale="locale"
                        :is-backoffice="isBackoffice"
                        @update-answer="onUpdateAnswer"
                    />

                    <!-- Success -->
                    <wizard-success
                        v-else
                        :key="'success'"
                    />
                </transition>
            </div>

            <!-- Navigation -->
            <div v-if="activeStep < visibleSteps.length" class="cw-nav">
                <button
                    v-if="activeStep > 0"
                    class="cw-btn cw-btn--secondary"
                    @click="prevStep"
                >
                    <i class="fal fa-arrow-left mr-2"></i>
                    {{ $t('forms.previous') }}
                </button>
                <span v-else></span>

                <button
                    v-if="activeStep < visibleSteps.length - 1"
                    class="cw-btn cw-btn--primary"
                    @click="nextStep"
                >
                    {{ $t('forms.next') }}
                    <i class="fal fa-arrow-right ml-2"></i>
                </button>
                <button
                    v-else
                    class="cw-btn cw-btn--success"
                    :disabled="sending"
                    @click="submit"
                >
                    <i :class="sending ? 'fal fa-spinner-third fa-spin mr-2' : 'fal fa-paper-plane mr-2'"></i>
                    {{ sending ? $t('forms.loading') : $t('wizard.send') }}
                </button>
            </div>
        </template>

        <!-- Error -->
        <div v-else class="cw-error">
            <i class="fal fa-exclamation-triangle"></i>
            <p>{{ $t('error.title') }}</p>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import './wizard-fields.css'
import { mapActions, mapState } from 'vuex'
import WizardStep from './WizardStep.vue'
import WizardSuccess from './WizardSuccess.vue'

export default {
    name: 'CustomWizard',
    components: { WizardStep, WizardSuccess },
    props: {
        formId: {
            type: [String, Number],
            default: null
        },
        postId: {
            type: [String, Number],
            default: null
        },
        // Backoffice/operator context: hidden-for-citizen fields must still be
        // rendered so the operator can fill them. Public flow leaves this false.
        isBackoffice: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: true,
            sending: false,
            activeStep: 0,
            answers: {},
            completedSteps: [],
            validationErrors: {}
        }
    },
    computed: {
        ...mapState('form', {
            form: (state) => state.form
        }),
        isEditMode() {
            return !!this.postId
        },
        // A step flagged `hidden` is reserved for the back-office: the agent still
        // sees and fills it in the CRM, the citizen never gets it. Same flag and
        // same meaning as `hidden` on an attribute.
        visibleSteps() {
            const steps = (this.form && this.form.steps) || []
            if (this.isBackoffice) return steps
            return steps.filter((step) => !step.hidden)
        },
        locale() {
            return this.$i18n.locale || 'fr'
        },
        formTitle() {
            if (!this.form) return ''
            if (this.form.translations) {
                const t = this.form.translations.find((tr) => tr.locale === this.locale)
                if (t && t.name) return t.name
            }
            if (this.form[this.locale] && this.form[this.locale].name) return this.form[this.locale].name
            return this.form.name || ''
        },
        formDescription() {
            if (!this.form) return ''
            if (this.form.translations) {
                const t = this.form.translations.find((tr) => tr.locale === this.locale)
                if (t && t.description) return t.description
            }
            if (this.form[this.locale] && this.form[this.locale].description) return this.form[this.locale].description
            return this.form.description || ''
        },
        resolvedFormId() {
            return this.formId || this.$route.params.id || this.$route.params.formId
        }
    },
    async created() {
        if (!this.resolvedFormId) return
        this.loading = true
        try {
            await this.getForm(this.resolvedFormId)
            this.initAnswers()
            this.completedSteps = new Array(this.visibleSteps.length).fill(false)

            if (this.isEditMode) {
                await this.loadPostAnswers()
            } else {
                // this.applyPrefillAnswers() // TODO: re-enable when backend supports answer duplication
            }
        } catch (e) {
            this.$store.dispatch('dispatchError', e)
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapActions('form', ['getForm']),
        ...mapActions('post', ['sendPost', 'updatePost']),

        // -- Load existing post answers for edit mode --
        loadPostAnswers() {
            const post = this.$store.state.post.post
            if (!post) return

            // Set post id in store for updatePost
            this.$store.state.post.post = { ...post, attributes: [] }

            if (post.answers && post.answers.length) {
                post.answers.forEach((answer) => {
                    let value = answer.value
                    // Parse JSON strings (e.g. category objects stored as '{"label":"..."}')
                    if (typeof value === 'string') {
                        try {
                            const parsed = JSON.parse(value)
                            if (typeof parsed === 'object') value = parsed
                        } catch (e) { /* keep as string */ }
                    }
                    this.$set(this.answers, answer.form_attribute_id, value)
                })
            }
        },

        // -- Pre-fill answers from a duplicated post (excludes media and
        // automatically captured fields) --
        applyPrefillAnswers() {
            const prefill = this.$store.state.wizard.prefillAnswers
            if (!prefill) return

            // Media can't be copied over, and an automatic geolocation (19) must
            // be captured again rather than inherit the duplicated post's position.
            const SKIPPED_TYPES = [12, 18, 19]
            const skippedAttrIds = new Set()
            if (this.form && this.form.steps) {
                this.form.steps.forEach((step) => {
                    (step.attributes || []).forEach((attr) => {
                        if (SKIPPED_TYPES.includes(attr.form_attribute_type_id)) {
                            skippedAttrIds.add(attr.id)
                        }
                    })
                })
            }

            prefill.forEach((answer) => {
                if (skippedAttrIds.has(answer.form_attribute_id)) return
                let value = answer.value
                if (typeof value === 'string') {
                    try {
                        const parsed = JSON.parse(value)
                        if (typeof parsed === 'object') value = parsed
                    } catch (e) { /* keep as string */ }
                }
                this.$set(this.answers, answer.form_attribute_id, value)
            })

            this.$store.commit('wizard/setPrefillAnswers', null)
        },

        // -- Init answers object keyed by attribute id --
        initAnswers() {
            if (!this.form || !this.form.steps) return
            this.form.steps.forEach((step) => {
                (step.attributes || []).forEach((attr) => {
                    this.$set(this.answers, attr.id, null)
                })
            })
        },

        // -- Get step name for current locale --
        getStepName(step) {
            if (step.translations) {
                const t = step.translations.find((tr) => tr.locale === this.locale)
                if (t && t.name) return t.name
            }
            return step.name || ''
        },

        // -- Answer update from field --
        onUpdateAnswer({ attributeId, value }) {
            this.$set(this.answers, attributeId, value)
        },

        // -- Validation --
        validateCurrentStep() {
            if (!this.$refs.currentStep) return true
            return this.$refs.currentStep.validate()
        },

        // -- Navigation --
        goToStep(index) {
            if (this.completedSteps[index] || index < this.activeStep) {
                this.activeStep = index
            }
        },
        nextStep() {
            if (!this.validateCurrentStep()) return
            this.$set(this.completedSteps, this.activeStep, true)
            this.activeStep++
            window.scrollTo(0, 0)
        },
        prevStep() {
            if (this.activeStep > 0) this.activeStep--
        },

        // -- Submit --
        async submit() {
            if (!this.validateCurrentStep()) return
            this.sending = true

            // Build post payload as array with id, type_id, value, as
            const attributes = []
            this.form.steps.forEach((step) => {
                (step.attributes || []).forEach((attr) => {
                    const val = this.answers[attr.id]
                    const isHidden = attr.hidden === 1 || attr.form_attribute_type_id === 2
                    // Hidden fields have no UI input — always include them so the backend
                    // receives the full attribute list and can apply its own rules.
                    if (val !== null && val !== undefined || isHidden) {
                        const config = attr.config ? (typeof attr.config === 'string' ? JSON.parse(attr.config) : attr.config) : {}
                        const entry = {
                            id: attr.id,
                            type_id: attr.form_attribute_type_id,
                            value: val !== null && val !== undefined ? val : null
                        }
                        if (config.as) entry.as = config.as
                        attributes.push(entry)
                    }
                })
            })

            this.$store.state.post.post = {
                ...this.$store.state.post.post,
                attributes
            }

            try {
                const postId = this.$store.state.post.post.id
                if (postId && postId > 0) {
                    await this.updatePost(this.resolvedFormId)
                } else {
                    await this.sendPost(this.resolvedFormId)
                }
                this.activeStep = this.visibleSteps.length
            } catch (error) {
                if (error.response && error.response.data && error.response.data.errors) {
                    this.handleValidationError(error.response.data)
                } else {
                    this.$store.dispatch('dispatchError', error)
                }
            } finally {
                this.sending = false
            }
        },

        handleValidationError(data) {
            let minStep = -1
            for (const key of Object.keys(data.errors)) {
                const parts = key.split('.')
                const attrId = parseInt(parts[parts.length - 1])

                // Indexes must stay in the same space as `activeStep`, i.e. the
                // steps actually rendered — back-office steps are filtered out.
                for (let i = 0; i < this.visibleSteps.length; i++) {
                    const found = (this.visibleSteps[i].attributes || []).find((a) => a.id === attrId)
                    if (found) {
                        if (i < minStep || minStep === -1) minStep = i
                        break
                    }
                }
            }
            if (minStep !== -1) this.activeStep = minStep

            this.$store.dispatch('toast', {
                type: 'error',
                title: this.$t('error.title'),
                message: this.$t('error.validation.retry')
            })
        }
    }
}
</script>

<style scoped>
.cw-wizard {
    width: 100%;
    max-width: 880px;
    margin: 0 auto;
    padding: 32px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

/* -- Loading -- */
.cw-loading {
    display: flex;
    justify-content: center;
    padding: var(--v2-space-6) 0;
    color: var(--v2-text-3);
    font-size: 1.5rem;
}

/* -- Error -- */
.cw-error {
    text-align: center;
    padding: var(--v2-space-6);
    color: var(--v2-text-4);
    background: var(--v2-surface-0);
    border: 1px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
}

.cw-error i {
    font-size: 2rem;
    margin-bottom: 12px;
    display: block;
}

.cw-error p {
    margin: 0;
    font-size: var(--v2-text-sm-size);
}

/* -- Form header -- */
.cw-form-header {
    text-align: center;
    margin-bottom: var(--v2-space-6);
}

.cw-form-title {
    font-size: 1.5rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    margin: 0 0 8px;
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.cw-form-desc {
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.6;
    max-width: 640px;
    margin: 0 auto;
}

/* -- Stepper -- */
.cw-stepper {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: var(--v2-space-5);
    padding: 16px 22px;
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
}

.cw-stepper-item {
    display: flex;
    align-items: center;
    flex: 1;
    cursor: default;
}

.cw-stepper-item:last-child {
    flex: 0 0 auto;
}

.cw-stepper-item.is-clickable {
    cursor: pointer;
}

.cw-stepper-item.is-clickable:hover .cw-stepper-icon {
    transform: scale(1.04);
}

.cw-stepper-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--v2-surface-0);
    color: var(--v2-text-4);
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-bold);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 240ms cubic-bezier(0.22, 1, 0.36, 1);
    border: 2px solid var(--v2-border);
    font-variant-numeric: tabular-nums;
}

.cw-stepper-icon i {
    color: inherit;
}

.cw-stepper-label {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-3);
    margin-left: 10px;
    white-space: nowrap;
    transition: color 240ms ease;
}

.cw-stepper-line {
    flex: 1;
    height: 2px;
    background: var(--v2-border);
    margin: 0 14px;
    border-radius: 2px;
    transition: background 240ms ease;
}

/* Active */
.cw-stepper-item.is-active .cw-stepper-icon {
    background: var(--v2-primary);
    color: #fff;
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 4px var(--v2-primary-soft);
}

.cw-stepper-item.is-active .cw-stepper-label {
    color: var(--v2-text-1);
}

/* Done */
.cw-stepper-item.is-done .cw-stepper-icon {
    background: var(--v2-success);
    color: #fff;
    border-color: var(--v2-success);
}

.cw-stepper-item.is-done .cw-stepper-label {
    color: var(--v2-text-2);
}

.cw-stepper-item.is-done .cw-stepper-line {
    background: var(--v2-success);
}

/* -- Content -- */
.cw-content {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    min-height: 320px;
    padding: 32px;
}

/* -- Navigation -- */
.cw-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: var(--v2-space-4);
    gap: var(--v2-space-3);
}

.cw-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 20px;
    border: 1px solid transparent;
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    font-family: inherit;
}

.cw-btn i {
    font-size: 0.8rem;
}

.cw-btn--primary {
    background: var(--v2-primary);
    color: #fff !important;
}

.cw-btn--primary i { color: #fff !important; }

.cw-btn--primary:hover:not(:disabled) {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.cw-btn--secondary {
    background: transparent;
    color: var(--v2-text-2);
    border-color: var(--v2-border);
}

.cw-btn--secondary:hover:not(:disabled) {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.cw-btn--success {
    background: var(--v2-success);
    color: #fff !important;
}

.cw-btn--success i { color: #fff !important; }

.cw-btn--success:hover:not(:disabled) {
    background: #059669;
    box-shadow: var(--v2-shadow-sm);
}

.cw-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none !important;
}

/* -- Transition -- */
.cw-slide-enter-active {
    transition: all 280ms cubic-bezier(0.22, 1, 0.36, 1);
}

.cw-slide-leave-active {
    transition: all 180ms ease;
}

.cw-slide-enter,
.cw-slide-leave-to {
    transform: translateY(10px);
    opacity: 0;
}

@media (max-width: 900px) {
    .cw-wizard {
        padding: 20px 18px 32px;
    }

    .cw-form-title {
        font-size: 1.25rem;
    }

    .cw-stepper {
        padding: 12px 14px;
    }

    .cw-stepper-label {
        display: none;
    }

    .cw-stepper-line {
        margin: 0 8px;
    }

    .cw-content {
        padding: 20px;
        border-radius: var(--v2-radius-md);
    }

    .cw-btn {
        padding: 0 14px;
        height: 36px;
    }
}
</style>
